import { useMemo } from 'react';

import { useFormContext, useWatch } from 'react-hook-form';

import { TemplateStudioModel } from 'common/contracts';
import { templateStudioStateService } from 'state/template-studio.state.service';
import { z } from 'zod';

import { BundleDesignsMetadata } from 'components/TemplatesStudio/DesignsMetadata/types';
import { ETemplateStudioBundlesViewModel } from 'components/TemplatesStudio/enums';

import { templateStudioSchema } from '../../../validation-schema';
import { DesignSidebarItem } from '../Components/DesignSidebarItem';

const BUNDLE_OBJECT_PATH = 'bundle';

const getBundlesDesignsByFilter = ({
  offersPerRow,
  themes
}: {
  offersPerRow: TemplateStudioModel['bundle']['offersPerRow'];
  themes: TemplateStudioModel['bundle']['themes'];
}): BundleDesignsMetadata[] => {
  const relevantBundleDesigns =
    templateStudioStateService.relevantBundleDesigns.get() || [];

  return relevantBundleDesigns.filter((currentDesign) => {
    return (
      // If offersPerRow is provided, check if the current design matches
      ((!!offersPerRow && currentDesign.bundleViewModel === offersPerRow) ||
        !offersPerRow) &&
      // Check if the current design supports the themes that the user filtered by
      (!themes || themes.length === 0 || themes.includes(currentDesign.theme))
    );
  });
};

export const BundleDesignsSecondarySidebar: React.FC = () => {
  const { control, setValue } =
    useFormContext<z.infer<typeof templateStudioSchema>>();

  const bundleObject: TemplateStudioModel[typeof BUNDLE_OBJECT_PATH] = useWatch(
    {
      control,
      name: BUNDLE_OBJECT_PATH
    }
  );

  const { themes, offersPerRow } = bundleObject;

  const filteredDesigns = useMemo(() => {
    return getBundlesDesignsByFilter({
      offersPerRow,
      themes
    });
  }, [themes, offersPerRow]);

  const setViewModel = (viewModel: ETemplateStudioBundlesViewModel) => {
    setValue(`${BUNDLE_OBJECT_PATH}.bundleViewModel`, viewModel, {
      shouldValidate: true,
      shouldDirty: true
    });
  };

  return (
    <>
      {filteredDesigns.map((design) => (
        <DesignSidebarItem
          key={design.id}
          {...design}
          blockDesignIdPath="bundle.selectedDesignId"
          onDesignSelect={() => setViewModel(design.bundleViewModel)}
        />
      ))}
    </>
  );
};
